import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { Reveal } from "@/components/Reveal";
import { SERVICES, waLink } from "@/data/content";

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);
  const askQuote = () =>
    navigate({ pathname: "/", hash: "#orcamento" }, { state: { service: slug } });

  return (
    <>
      <SEO title={service.metaTitle} description={service.metaDescription} path={`/servicos/${slug}`} />
      <Header />
      <main data-testid="service-page" className="pt-28 sm:pt-36">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <Link
              to="/"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-moss transition-colors hover:text-pine"
            >
              <ArrowLeft size={16} /> Todos os serviços
            </Link>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 items-end gap-10 lg:grid-cols-2">
            <Reveal delay={0.05}>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-lime-600">
                Serviços EcoJard
              </p>
              <h1 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
                {service.title}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-moss sm:text-lg">
                {service.intro}
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-3xl border border-stone2 shadow-[0_32px_70px_-30px_rgba(18,37,26,0.3)]">
                <img
                  src={service.image}
                  alt={service.title}
                  className="h-64 w-full object-cover sm:h-80"
                  loading="eager"
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <h2 className="font-serif text-2xl text-ink sm:text-3xl">O que está incluído</h2>
              <ul className="mt-7 flex flex-col gap-4">
                {service.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-ink">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime-100 text-lime-700">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-sand/70 p-8 sm:p-10">
                <h2 className="font-serif text-2xl text-ink">Como trabalhamos</h2>
                <p className="mt-5 text-base leading-relaxed text-moss">{service.closing}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    data-testid="service-quote-button"
                    onClick={askQuote}
                    className="group flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-pine-hover"
                  >
                    Pedir orçamento gratuito
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <a
                    data-testid="service-whatsapp-button"
                    href={waLink(`Olá EcoJard! Gostaria de um orçamento para ${service.title}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5A]"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.05}>
            <div className="mt-20 border-t border-stone2 pb-24 pt-12">
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-moss">
                Outros serviços
              </h2>
              <div className="mt-6 flex flex-wrap gap-3">
                {others.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/servicos/${s.slug}`}
                    data-testid={`other-service-chip-${s.slug}`}
                    className="rounded-full border border-stone2 bg-white px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:border-pine hover:text-pine"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}
